"use client";

import React from "react";
import { Check, Loader, ArrowRight, Phone, Mail, Clock } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const inputStyles = "flex h-12 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-slate-900";
const textareaStyles = "flex min-h-[120px] w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y text-slate-900";
export default function StartYourKitchenProject() {
  return <section data-section-id="4103" id="request-your-free-site-visit" className="dark bg-background text-foreground w-full">
      <div className="grid lg:grid-cols-2 min-h-[800px]">
        {}
        <div className="bg-secondary text-secondary-foreground p-8 md:p-16 lg:p-24 flex flex-col justify-center relative overflow-hidden">
          {}
          <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
          
          <div className="relative z-10 max-w-xl mx-auto lg:mx-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-default font-bold tracking-widest uppercase mb-6">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              California Licensed & Bonded
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-default font-bold leading-tight mb-6">
              Call Us Today<br />
              <span className="text-primary">(925) 529-5005</span>
            </h2>
            
            <p className="text-lg text-secondary-foreground/80 mb-8 leading-relaxed font-default">
              PCH Builders has been the premier deck contractor for the Bay Area and Central Coast since 1990. We specialize in high-end composite, wood, and structural decks built to withstand the California elements.
            </p>

            <div className="space-y-6 mb-12">
              {["Licensed & Bonded since 1990", "Custom Trex & Wood Deck Designs", "Structural & Concrete Deck Experts", "Free Comprehensive Site Consultations"].map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                  <div className="mt-1 bg-primary/20 p-1 rounded-full">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-secondary-foreground/90 font-default font-medium">{item}</span>
                </div>)}
            </div>

            <div className="pt-8 border-t border-secondary-foreground/10">
              <p className="text-xs font-default font-bold text-secondary-foreground/60 uppercase tracking-widest mb-4">
                Direct Contact
              </p>
              <div className="flex flex-col sm:flex-row gap-6">
                <Link to="tel:925-529-5005" className="flex items-center gap-3 hover:text-primary transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-secondary-foreground/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="block text-xs opacity-70 font-default">Call Our Office</span>
                    <span className="font-default font-bold">(925) 529-5005</span>
                  </div>
                </Link>
                <Link to="mailto:info@pchbuilders.com" className="flex items-center gap-3 hover:text-primary transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-secondary-foreground/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="block text-xs opacity-70 font-default">Email Inquiries</span>
                    <span className="font-default font-bold">info@pchbuilders.com</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="bg-slate-50 text-slate-900 p-8 md:p-16 lg:p-24 flex flex-col justify-center">
          <div className="max-w-xl mx-auto w-full">
            <div className="mb-8">
              <h3 className="text-3xl font-default font-bold mb-2 text-secondary">Request Your Free Site Visit</h3>
              <p className="text-slate-600 font-default">
                Share your project goals. Our team will review your details and contact you to schedule a professional on-site consultation.
              </p>
            </div>

            <div className="bg-white p-6 md:p-8 rounded-sm shadow-xl border border-slate-200">
              <FormProvider formKey="site_visit_form_wood_deck_builders" formId="site_visit_form_wood_deck_builders" sectionName="deck-consultation-cta" defaultValues={{
              name: "",
              email: "",
              phone: "",
              budget: "",
              timeline: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              budget: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Request Site Visit" successMessage="Thank you! Your request for a free site visit has been received. One of our deck specialists will contact you within 24 hours to schedule your consultation.">
                <KitchenProjectForm />
              </FormProvider>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500 flex items-center justify-center gap-2 font-default">
                <Clock className="w-3 h-3" />
                <span>Response time: Typically under 24 business hours</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function KitchenProjectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12 space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8" />
        </div>
        <h4 className="text-2xl font-default font-bold text-secondary">Consultation Requested</h4>
        <p className="text-slate-600 max-w-xs mx-auto font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 font-default border-secondary text-secondary hover:bg-secondary hover:text-white">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-700 font-default font-bold">Your Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Jane Smith" className={inputStyles} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-700 font-default font-bold">Email Address <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="jane@example.com" className={inputStyles} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-700 font-default font-bold">Phone Number <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="(925) 555-0123" className={inputStyles} />
              </FormControl>
              <FormDescription className="text-xs text-slate-500 font-default">
                Required for scheduling your site visit.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-700 font-default font-bold">Wood Type Interest <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <select {...field} className={inputStyles}>
                  <option value="" disabled>Select material type</option>
                  <option value="Composite & Trex">Composite & Trex Decks</option>
                  <option value="Natural Wood & Cedar">Natural Wood & Cedar</option>
                  <option value="Pool & Concrete">Pool & Concrete Decks</option>
                  <option value="Structural & Concrete">Structural & Multi-Level</option>
                </select>
              </FormControl>
              <FormDescription className="text-xs text-slate-500 font-default">
                Select the primary material interest for your project.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="timeline" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-700 font-default font-bold">Project Zip Code</FormLabel>
            <FormControl>
              <input {...field} type="text" placeholder="e.g., 95060" className={inputStyles} />
            </FormControl>
            <FormDescription className="text-xs text-slate-500 font-default">
              Helps us assign the right local estimator for your area.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-700 font-default font-bold">Project Details <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Tell us about your deck vision, preferred materials, and any specific requirements for your site visit..." className={textareaStyles} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base bg-primary hover:bg-primary/90 text-primary-foreground font-default font-bold rounded-sm shadow-lg transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Sending Request...
          </> : <>
            Request Site Visit
            <ArrowRight className="ml-2 h-4 w-4" />
          </>}
      </Button>
    </Form>;
}